/* This module looks after all of the authentication.

It contains:

- The store where the token and user details are saved
- The actions to login and logout
- The mutations and getters needed to support these purposes.

*/

import Vue from "vue";
import { VueOfflineStorage } from "vue-offline";
import * as jwtDecode from "jwt-decode";
import { authHTTP, TOKEN_HEADER_STRING } from "@/services/axios.service";
import router from "@/router";
import * as authService from "@/services/auth.service";
import { HandleFilters, MutateFilter } from "@/services/filters.service";
import { handleError } from "@/functions/handleError.function";
import { getCookie } from "@/functions/cookies.function";
import deleteOfflineKey from "@/functions/DelKeyVueOffline.function";

const state = {
  tokens: {},
  userProfile: {
    id: "",
    groups: []
  },
  permissions: [],
  users: [],
  userList: [],
  filters: {},
  TOKEN_KEY: "access_token"
};

// getters
const getters = {
  tokens: state => {
    return state.tokens;
  },
  tokenData: state => {
    return jwtDecode(state.tokens.access);
  },
  authenticated: state => {
    return !!state.tokens.access;
  },
  userProfile: state => {
    return state.userProfile;
  },
  permissions: state => {
    return state.permissions;
  },
  users: state => {
    // This is the list used to populate the menu items
    return state.users;
  },
  userList: state => {
    // This is the list used for the User Index page
    return state.userList;
  },
  filters: state => {
    return state.filters;
  },
  search: state => {
    return state.filters.search;
  }
};

// mutations
const mutations = {
  setAccessToken(state, value) {
    Vue.set(state.tokens, "access", value);
  },
  setUserProfile(state, value) {
    state.userProfile = value;
  },
  setPermissions(state, value) {
    state.permissions = value;
  },
  setUsers(state, value) {
    state.users = value;
  },
  clearAuthState() {
    state.tokens = {};
    state.permissions = [];
    state.userProfile = {
      groups: []
    };
    state.users = [];
    state.userList = [];
  },
  filters(state, payload) {
    MutateFilter(state, payload);
  }
};

// actions
const actions = {
  login(context, payload) {
    context.commit("core/clearErrors", {}, { root: true });
    // payload: username, password
    return authService
      .getToken(payload.username, payload.password)
      .then(function(response) {
        // Save the Token (if there's a Token we're logged in!)
        context.commit("setAccessToken", response.data.access);
        // Update the token in the headers of the axios instance
        authHTTP.defaults.headers.common["Authorization"] =
          TOKEN_HEADER_STRING + context.getters["tokens"].access;
        // Persist the *fact* that a refresh cookie *exists*
        localStorage.setItem(
          "has_refresh_cookie",
          getCookie("has_refresh_cookie")
        );
        // Get user info. Use a cache-first strategy for everything *except*
        // for the User's own profile and permissions
        context.dispatch("getUserProfile");
        context.dispatch("getPermissions");
        context.dispatch("getUsers");
        // Other UI/UX data (cache-first)
        context.dispatch("core/getResults", {}, { root: true });
        context.dispatch("core/getAircraft", {}, { root: true });

        context.dispatch("exams/getExamTemplates", {}, { root: true });
        context.dispatch("questions/getTopics", {}, { root: true });

        context.dispatch("records/getMarkers", {}, { root: true });
        context.dispatch("records/getReasonCodes", {}, { root: true });
        context.dispatch("records/getTasks", {}, { root: true });
        context.dispatch("records/getCompetencies", {}, { root: true });
        // Redirect =)
        router.push(
          { name: "dashboard" },
          () => {},
          () => {}
        );
        return true;
      })
      .catch(function(error) {
        var data = handleError(error);
        context.commit("core/setError", data, { root: true });
        return false;
      });
  },
  logout(context) {
    authService.logout(); // Logout from the server to clear cookies
    localStorage.setItem("has_refresh_cookie", undefined);
    // Clear app state
    context.commit("clearAuthState");
    context.commit("core/clearState", {}, { root: true });
    context.commit("exams/clearState", {}, { root: true });
    context.commit("records/clearState", {}, { root: true });
    context.commit("courses/clearState", {}, { root: true });
    // Clear offline cache
    let sensitiveKeys = ["users", "userProfile", "permissions", "userList"];
    if (JSON.parse(VueOfflineStorage.keys)) {
      for (let key of JSON.parse(VueOfflineStorage.keys)) {
        if (sensitiveKeys.includes(key)) {
          localStorage.removeItem(key, "");
          deleteOfflineKey(key);
        }
      }
    }
  },
  refreshToken(context) {
    return authService
      .refreshToken()
      .then(function(response) {
        context.commit("setAccessToken", response.data.access);
        // Update the token in the headers of the axios instance
        authHTTP.defaults.headers.common["Authorization"] =
          TOKEN_HEADER_STRING + context.getters["tokens"].access;
        context.dispatch("getUserProfile");
        context.dispatch("getUsers");
        context.dispatch("getPermissions");
        return response;
      })
      .catch(function(error) {
        var data = handleError(error);
        context.commit("core/setError", data, { root: true });
        return error;
      });
  },
  getUserProfile(context) {
    // Get the userProfile for the logged in user
    const token_data = context.getters["tokenData"];
    authService
      .getUserProfile(token_data.user_id)
      .then(function(response) {
        context.commit("setUserProfile", response.data);
        VueOfflineStorage.set("userProfile", response.data);
      })
      .catch(function(error) {
        var data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getPermissions(context) {
    authService
      .getPermissions()
      .then(function(response) {
        context.commit("setPermissions", response.data.permissions);
        VueOfflineStorage.set("permissions", response.data.permissions);
      })
      .catch(function(error) {
        var data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getUsers(context) {
    // Get the list of active pilot and instructor users
    authService
      .getUsers("?is_active=1")
      .then(function(response) {
        context.commit("setUsers", response.data);
        VueOfflineStorage.set("users", response.data);
      })
      .catch(function(error) {
        var data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getUserList(context) {
    // Get the list of Users for the User Index view
    const pageSize = context.getters["core/pageSize"];
    const userFilter = HandleFilters(state.filters, pageSize);
    authService
      .getUsers(userFilter)
      .then(function(response) {
        state.userList = response.data;
        VueOfflineStorage.set("userList", response.data);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
